import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Users, Clock } from "lucide-react";
import useCampuses from "../hooks/useCampuses";
import useGrades from "../hooks/useGrades";
import SEO from "../components/SEO";

const curriculumColors = {
  CBSE: "bg-orange-100 text-orange-700 border-orange-200",
  IB: "bg-purple-100 text-purple-700 border-purple-200",
  Cambridge: "bg-blue-100 text-blue-700 border-blue-200",
};

const CampusCard = ({ campus }) => {
  const grades = useGrades(campus.id);
  const computedRange =
    grades.length >= 2
      ? `${grades[0]} to ${grades[grades.length - 1]}`
      : grades.length === 1
      ? grades[0]
      : "Nursery to Grade XII";
  const gradeRange = campus.gradeRangeLabel || computedRange;

  return (
    <div
      id={campus.id}
      className={`bg-white rounded-2xl border-2 overflow-hidden hover:shadow-xl transition-all duration-300 ${
        campus.featured ? "border-[#C83C4A]" : "border-gray-100"
      }`}
    >
      {/* Campus header */}
      <div className={`px-6 py-5 flex items-center gap-4 ${campus.featured ? "bg-[#9B1522]" : "bg-[#1A0505]"}`}>
        <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center text-white font-extrabold text-base flex-shrink-0 border border-white/20">
          {campus.code.slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-base leading-tight">{campus.name}</h3>
          <p className="text-[#D97481] text-xs font-semibold mt-0.5">{campus.area}</p>
        </div>
        {campus.featured && (
          <span className="flex-shrink-0 bg-white text-[#9B1522] text-xs font-bold px-2.5 py-1 rounded-full">
            Flagship
          </span>
        )}
      </div>

      <div className="p-6">
        <p className="text-gray-600 text-sm leading-relaxed mb-5">{campus.description}</p>

        <div className="space-y-3 mb-5">
          <div className="flex items-start gap-3">
            <MapPin size={15} className="text-[#B41C2C] mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-600">{campus.address}</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone size={15} className="text-[#B41C2C] flex-shrink-0" />
            <a href={`tel:${campus.phone}`} className="text-sm text-gray-600 hover:text-[#B41C2C] transition-colors">
              {campus.phone}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Users size={15} className="text-[#B41C2C] flex-shrink-0" />
            <span className="text-sm text-gray-600" data-testid={`campus-grades-${campus.id}`}>{gradeRange}</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock size={15} className="text-[#B41C2C] flex-shrink-0" />
            <span className="text-sm text-gray-600">Mon – Sat, 08:00 – 17:00</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {campus.curricula.map((c) => (
              <span
                key={c}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                  curriculumColors[c] || "bg-gray-100 text-gray-700 border-gray-200"
                }`}
              >
                {c}
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <a
              href={campus.mapLink}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-[#B41C2C] hover:text-[#9B1522] border border-[#F8C5C9] px-3 py-1.5 rounded-lg transition-colors"
            >
              View Map
            </a>
            <a
              href={`tel:${campus.phone}`}
              className="text-xs font-semibold bg-[#9B1522] hover:bg-[#87111E] text-white px-3 py-1.5 rounded-lg transition-colors"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Campuses = () => {
  const campuses = useCampuses();

  return (
    <main>
      <SEO
        title="Our Campuses | 6 CBSE Schools Across Bangalore"
        description="TIPS Bengaluru has 6 campuses across Bangalore – HBR Layout, Nagawara, Yelahanka, Thanisandra and more. Find your nearest campus and book a free visit."
        keywords="TIPS Bengaluru campuses, CBSE school Nagawara, CBSE school HBR Layout, TIPS school Yelahanka, TIPS school Thanisandra, CBSE school near me Bangalore"
        canonical="/campuses"
      />
      {/* Page Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Campuses</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Our Campuses
          </h1>
          <p className="text-gray-400 mt-3 max-w-2xl">
            {campuses.length} campus{campuses.length !== 1 ? "es" : ""} across Bengaluru, each offering world-class CBSE education in a nurturing, safe environment.
          </p>
        </div>
      </section>

      {/* Campuses Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {campuses.map((campus) => (
                <CampusCard key={campus.id} campus={campus} />
              ))}
            </div>
        </div>
      </section>

      {/* Admission CTA */}
      <section className="py-16 bg-[#9B1522]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Find Your Nearest Campus
          </h2>
          <p className="text-[#FEE7E9] mb-7">Book a free campus tour and experience TIPS education first-hand.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/917619305034?text=Hi!%20I'd%20like%20to%20book%20a%20campus%20tour%20at%20TIPS%20Bengaluru."
              target="_blank"
              rel="noreferrer"
              className="bg-white text-[#87111E] font-bold px-8 py-3 rounded-xl hover:bg-gray-100 transition-colors"
            >
              Book a Campus Tour
            </a>
            <Link
              to="/admissions"
              className="border-2 border-white/50 hover:border-white text-white font-bold px-8 py-3 rounded-xl hover:bg-white/10 transition-colors"
            >
              Apply Online
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Campuses;